import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Linkedin, RefreshCw, Unplug, Copy } from "lucide-react";
import { TestPagePanel } from "@/components/TestPagePanel";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "long", year: "numeric" }) : "");
const btn = "inline-flex items-center gap-2 border border-slate-300 px-3 py-2 rounded-sm text-xs font-semibold hover:border-coral disabled:opacity-40";

const PageSelect = ({ value, pages, onChange, testid, exclude = [] }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white max-w-full">
    <option value="">Aucune page</option>
    {pages.filter((p) => !exclude.includes(p.urn) || p.urn === value).map((p) => <option key={p.urn} value={p.urn}>{p.nom}</option>)}
  </select>
);
export { PageSelect };

const Account = ({ st, onReload }) => {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    try { const { data } = await api.get("/admin/conferences/linkedin/auth-url"); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/conferences/pages/actualiser"); toast.success(`${data.pages} page(s) administrée(s)`); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const disconnect = async () => {
    if (!window.confirm("Déconnecter LinkedIn (pages APOLLO) ? Plus aucune publication automatique ne partira.")) return;
    await api.post("/admin/conferences/linkedin/deconnecter"); onReload();
  };
  const c = st.compte;
  return (
    <div className="flex flex-wrap items-start gap-4 justify-between">
      <div className="text-sm space-y-1 min-w-0 flex-1 basis-64" data-testid="pages-account-status">
        {!st.configure && <p className="text-amber-800 font-semibold" data-testid="pages-not-configured">Identifiants de l'application « APOLLO Pages » pas encore saisis.</p>}
        {c ? <>
          <p className="font-semibold text-emerald-800">Connecté{c.par ? ` par ${c.par}` : ""}{c.connecte_le ? `, le ${fmt(c.connecte_le)}` : ""}</p>
          <p className={c.expire ? "text-red-700 font-semibold" : "text-slate-600"} data-testid="pages-token-expiry">Jeton {c.expire ? "expiré depuis" : "valable jusqu'au"} {fmt(c.expire_le)}{c.renouvellement_auto ? ` · renouvellement automatique jusqu'au ${fmt(c.renouvellement_jusqua)}` : " · pas de renouvellement automatique : reconnexion à prévoir"}</p>
        </> : st.configure && <p className="text-slate-600">Pas encore connecté.</p>}
        <p className="text-xs text-slate-400 flex flex-wrap items-center gap-1">Adresse de retour OAuth : <code className="break-all" data-testid="pages-redirect-uri">{st.adresse_retour}</code>
          <button onClick={() => { navigator.clipboard.writeText(st.adresse_retour); toast.success("Adresse copiée"); }} aria-label="Copier l'adresse" data-testid="pages-redirect-copy"><Copy className="h-3 w-3" /></button></p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={connect} disabled={!st.configure} data-testid="pages-connect-btn" className="inline-flex items-center gap-2 bg-[#0a66c2] text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-[#004182] disabled:opacity-40"><Linkedin className="h-4 w-4" /> {c ? "Reconnecter" : "Connecter"} LinkedIn (pages APOLLO)</button>
        {c && <button onClick={refresh} disabled={busy} data-testid="pages-refresh-btn" className={btn}><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Actualiser les pages</button>}
        {c && <button onClick={disconnect} data-testid="pages-disconnect-btn" className={btn}><Unplug className="h-3.5 w-3.5" /> Déconnecter</button>}
      </div>
    </div>
  );
};

const Config = ({ st, groupPages, onReload }) => {
  const save = async (patch) => {
    try { await api.put("/admin/conferences/pages/config", patch); toast.success("Enregistré"); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const { france_urn: france, test_urn: test, auto } = st.config;
  const toggleAuto = () => {
    if (!auto && !window.confirm("Activer la publication automatique ? Les posts prêts partiront sans clic, selon les horaires.")) return;
    save({ auto: !auto });
  };
  return (
    <div className="grid sm:grid-cols-3 gap-4 mt-5 text-sm">
      <label className="space-y-1"><span className="block text-xs uppercase tracking-[0.12em] text-slate-400">Page de republication « APOLLO France »</span>
        <PageSelect value={france} pages={st.pages} exclude={[test, ...groupPages]} onChange={(v) => save({ france_urn: v })} testid="pages-france-select" /></label>
      <label className="space-y-1"><span className="block text-xs uppercase tracking-[0.12em] text-slate-400">Page de test</span>
        <PageSelect value={test} pages={st.pages} exclude={[france, ...groupPages]} onChange={(v) => save({ test_urn: v })} testid="pages-test-select" /></label>
      <div className="space-y-1"><span className="block text-xs uppercase tracking-[0.12em] text-slate-400">Publication automatique</span>
        <button onClick={toggleAuto} data-testid="pages-auto-toggle" aria-pressed={auto} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-semibold border ${auto ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-50 border-slate-300 text-slate-600"}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${auto ? "bg-emerald-500" : "bg-slate-400"}`} />{auto ? "Activée" : "Coupée : chaque post attend « Publier »"}</button></div>
    </div>
  );
};

export const LinkedInPagesPanel = ({ st, groupPages, onReload }) => (
  <section className="bg-white border border-slate-200 rounded-sm p-5 mb-6" data-testid="linkedin-pages-panel">
    <Account st={st} onReload={onReload} />
    {st.compte && <Config st={st} groupPages={groupPages} onReload={onReload} />}
    {st.compte && st.config.test_urn && <TestPagePanel st={st} />}
  </section>
);

export default LinkedInPagesPanel;
